import { yupResolver } from '@hookform/resolvers/yup';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  IconButton,
  InputAdornment,
  ListItemText,
  Stack,
  SvgIcon,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import { Fragment, SyntheticEvent, useEffect, useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useBlocker, useLocation } from 'react-router';
import { InferType } from 'yup';

import {
  EmptyContent,
  enqueueSnackbar,
  FormProvider,
  FullPageSpinner,
  RHFAutocomplete,
  RHFDatePicker,
  RHFRadioGroup,
  RHFTextField,
  useBoolean,
  useDefaultEnumsStore,
  useDirtyFieldsStore,
  useFactFindConfigStore,
  useFactFindErrorStore,
  useFactFindProcessStore,
  useUserAccessStore,
  useUserDetailsStore,
} from '@zeta-core';
import { AddressIcon, AddSvgIcon, BasicDetailsIcon, countries, DependentsIcon, EmploymentIcon, PlusIcon, ResidencyIcon } from '@zeta-core/assets';
import {
  AIInteractionService,
  getErrorMessage,
  IFactFindProcessSectionType,
  IFactFindProcessStatus,
  InteractionSubSectionType,
  InvestorService,
  IPatchAIInteractionSectionRequest,
} from '@zeta-services';
import {
  FactFindType,
  ICaptureFactFindPersonalDetailsRequest,
  IClientCardItem,
  IFactFindEntity,
  IGenericErrorResponse,
  IViewFactFindPersonalDetailsRequest,
  IViewFactFindPersonalDetailsResponse,
} from '@zeta-types';

import { WarningRounded } from '@mui/icons-material';
import { ConflictsDialog } from '../../../components/conflicts-dialog';
import { getClientFeatureAccess } from '../../../helpers/access-control-helper';
import AllTranscripts from '../all-transcripts';
import { getLabel, yesNoObject } from '../common/commons';
import FactFindHeading from '../common/fact-find-heading';
import CommonTableLayout from '../components/common-table-layout';
import ConfirmNavigationDialog from '../components/confirmation-dialog';
import ErrorDialog from '../components/error-dialog';
import { ErrorSection } from '../components/error-section';
import { IGeneralFieldType, RenderArrayFieldWithChildren, renderGeneralFields } from '../components/render-fields';
import SaveDialog from '../components/save-dialog';
import SimpleFieldsTable from '../components/simple-fields-table';
import SuccessDialog from '../components/success-dialog';
import FactFindFooter from '../fact-find-layout/fact-find-footer';
import { useSourceControlContext } from '../fact-find-layout/sourcecontrol-context-component';
import Transcript from '../transcript';
import AddPartnerDialog from './add-partner-modal';
import { dependentDefaultValues } from './constants';
import { createPayload, personalInformationDefaultValues, personalInformationValidationSchema } from './form-helpers';
import { getErrorFieldName } from './helpers';
import { setFormData } from './set-form-data';

enum ISubSectionType {
  'basic' = 'basic',
  'employment' = 'employment',
  'address' = 'address',
  'residency' = 'residency',
  'dependent' = 'dependent',
}

export const PersonalInfo = ({
  isClient,
  isManagedClient,
  from,
  clientData,
  conflictData,
  refetch,
  updatedClientPartnerDetails,
  getDropdownOptions,
}: {
  isClient?: boolean;
  isManagedClient?: boolean;
  from: FactFindType;
  refetch?: () => void;
  getDropdownOptions?: () => void;
}) => {
  const userAccess = useUserAccessStore((state) => state.userAccess);
  const theme = useTheme();
  const isTranscript = useBoolean();
  const isAllTranscripts = useBoolean();
  const location = useLocation();
  const [currentTab, setCurrentTab] = useState('client');
  const { accessibleEntityID } = useUserDetailsStore((state) => state.user);
  let state = (location.state || {}) as { entityData: IFactFindEntity; clientData: IClientCardItem };
  if (isClient) {
    state = { clientData: { clientID: accessibleEntityID } };
  }
  const {
    data: sourceControlData,
    mutateAsync: sourceControlMutateAsync,
    isPending: sourceControlPending,
    clientPartnerDetails,
  } = useSourceControlContext() || {};
  const planningStatus = location?.state?.clientData?.planningStatus;
  const readOnly = getClientFeatureAccess(userAccess?.access, 'Advisor-Client-FF', planningStatus).isReadOnly;

  const ffConfig = useFactFindConfigStore((state) => state?.factFindConfigDetails?.individualFactFindSectionConfig?.personalInfo?.subsections);
  const defaultEnums = useDefaultEnumsStore((state) => state.defaultEnums);

  const titleOptions = defaultEnums?.titleType?.map((e) => e.titleType) ?? [];
  const maritalStatusOptions = defaultEnums?.maritalStatus?.map((e) => e.maritalStatus) ?? [];
  const employmentStatusOptions = defaultEnums?.employmentStatus?.map((e) => e.employmentStatus) ?? [];
  const residencyTypeOptions = defaultEnums?.residencyType?.map((e) => e.residencyType) ?? [];
  const genderOptions = defaultEnums?.genderType?.map((e) => e.genderType) ?? [];
  const countryOfBirthOptions = defaultEnums?.countryType?.map((e) => e.countryType) ?? [];

  const updateDirtyFieldsState = useDirtyFieldsStore((state) => state.updateDirtyFieldsState);
  const ffErrors = useFactFindErrorStore((state) => state?.ffErrors);
  const updateFFErrorStore = useFactFindErrorStore((state) => state?.updateFFErrorStore);
  const sectionsProcessDetails = useFactFindProcessStore((state) => state.sections);
  const currentSection = sectionsProcessDetails?.find((section) => section.section_name === IFactFindProcessSectionType.personal_details)?.status;
  const fieldErrors = ffErrors?.find((error) => error.section === InteractionSubSectionType.personal_details)?.missing_fields;
  const [selectedSubSection, setSelectSubSection] = useState<string>(ISubSectionType.basic);
  const [loading, setLoading] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [confirmNavigationModal, setConfirmNavigationModal] = useState(false);
  const [showPrompt, setShowPrompt] = useState(true);
  const [showModal, setShowModal] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [addPartnerModal, setAddPartnerModal] = useState<boolean>(false);
  const [conflictsDialog, setConflictsDialog] = useState<boolean>(false);

  const isReadOnly =
    from === FactFindType.PARA_PLANNER ||
    currentSection === IFactFindProcessStatus.pending ||
    currentSection === IFactFindProcessStatus.started ||
    isClient ||
    readOnly;

  type SchemaType = InferType<typeof personalInformationValidationSchema>;

  const methods = useForm<SchemaType>({
    defaultValues: personalInformationDefaultValues,
    resolver: yupResolver(personalInformationValidationSchema),
    mode: 'all',
    reValidateMode: 'onChange',
  });
  const { reset } = methods;

  const { fields: dependentFields, remove, append } = useFieldArray({ name: 'dependents', control: methods.control });

  const addressFieldArrayClient = useFieldArray({ name: 'client.addresses', control: methods.control });
  const taxResidencyFieldArrayClient = useFieldArray({ name: 'client.taxResidency', control: methods.control });
  const employmentFieldArrayClient = useFieldArray({ name: 'client.employmentDetails', control: methods.control });

  const addressFieldArrayPartner = useFieldArray({ name: 'partner.addresses', control: methods.control });
  const taxResidencyFieldArrayPartner = useFieldArray({ name: 'partner.taxResidency', control: methods.control });
  const employmentFieldArrayPartner = useFieldArray({ name: 'partner.employmentDetails', control: methods.control });

  const sameAsClient = useWatch({ control: methods.control, name: 'partner.addresses.0.sameAsClient' });
  const clientAddress = useWatch({ control: methods.control, name: 'client.addresses' });

  const viewPersonalDetails = useMutation({
    mutationFn: (payload: IViewFactFindPersonalDetailsRequest) => InvestorService().viewPersonalDetails(payload),

    onSuccess: (response: AxiosResponse<IViewFactFindPersonalDetailsResponse>) => {
      const { client, partner, dependents } = setFormData(response?.data, response?.data?.useClientAddressForPartner);
      const values = { client, partner, dependents };
      reset(values);
      blocker?.reset?.();
      setShowModal(false);
    },
  });

  useEffect(() => {
    if ([FactFindType.FACT_FIND, FactFindType.PARA_PLANNER].includes(from)) {
      viewPersonalDetails.mutateAsync({ clientID: state.clientData.clientID, forExternalUse: true });
    }
    if (from === FactFindType.AI) {
      const { client, partner, dependents } = setFormData(clientData?.clientData?.data, clientData?.sameAddressAsClient);
      const values = { client, partner, dependents };
      reset(values);
      blocker?.reset?.();
      setShowModal(false);
      setSelectSubSection(ISubSectionType.employment);
      setTimeout(() => {
        setSelectSubSection(ISubSectionType.basic);
      }, 10);
    }
  }, [reset, clientData, state.clientData.clientID, from, viewPersonalDetails.mutateAsync]);

  const capturePersonalDetails = useMutation({
    mutationFn: (payload: ICaptureFactFindPersonalDetailsRequest) => InvestorService().capturePersonalDetails(payload),
    onSuccess: (response: AxiosResponse<IViewFactFindPersonalDetailsRequest>) => {
      if (response.status === 200) {
        viewPersonalDetails.mutateAsync({ clientID: state.clientData.clientID });
        sourceControlMutateAsync({ clientID: state.clientData.clientID });
        setLoading(false);
        enqueueSnackbar('Personal Details saved successfully', { variant: 'success' });
      }
    },
    onError: (error: IGenericErrorResponse) => {
      setLoading(false);
      const errors = error?.response?.data?.error || error?.response?.data?.error[0];

      if (Array.isArray(errors) && errors.length && errors[0].errorCode !== 'I010001') {
        let message = '';
        if (errors[0].errorCode === 'I010004' || errors[0].errorCode === 'I010005')
          message = getErrorMessage(errors[0].errorCode, { country: errors[0].errorDetails?.split(' ')[5] });
        else message = getErrorMessage(errors[0].errorCode);
        setErrorMsg(message);
      } else if (Array.isArray(errors) && errors.length) {
        setErrorMsg('The required details are missing from the request. Please enter valid details and try again');
        errors.forEach((e) => {
          if (e.errorCode === 'I010001') {
            const field = getErrorFieldName(e?.errorDetails?.split(' ')[1]?.split('.')[0], e?.errorDetails?.split(' ')[1]?.split('.')[1]);

            methods.setError(`${field}`, { type: 'manual', message: 'This is required field' }, { shouldFocus: true });
          }
        });
      }
      setIsError(true);
      setTimeout(() => {
        setIsError(false);
        setErrorMsg('');
      }, 5000);
    },
  });

  const capturePersonalDetailsAI = useMutation({
    mutationFn: (payload: IPatchAIInteractionSectionRequest) => AIInteractionService().patchClientData(payload),
    onSuccess: (response: AxiosResponse) => {
      if (response.status === 200) {
        refetch?.();
        sourceControlMutateAsync({ clientID: state.clientData.clientID });
        setLoading(false);
        enqueueSnackbar('Personal Details saved successfully', { variant: 'success' });
        updateFFErrorStore({ ffErrors: null });
      }
    },
    onError: (error: IGenericErrorResponse) => {
      setLoading(false);
      const errors = error?.response?.data?.error || error?.response?.data?.error[0];

      if (Array.isArray(errors) && errors.length && errors[0].errorCode !== 'I010001') {
        let message = '';
        if (errors[0].errorCode === 'I010004' || errors[0].errorCode === 'I010005')
          message = getErrorMessage(errors[0].errorCode, { country: errors[0].errorDetails?.split(' ')[5] });
        else message = getErrorMessage(errors[0].errorCode);
        setErrorMsg(message);
      } else if (Array.isArray(errors) && errors.length) {
        setErrorMsg('The required details are missing from the request. Please enter valid details and try again');
        errors.forEach((e) => {
          if (e.errorCode === 'I010001') {
            const field = getErrorFieldName(e?.errorDetails?.split(' ')[1]?.split('.')[0], e?.errorDetails?.split(' ')[1]?.split('.')[1]);

            methods.setError(`${field}`, { type: 'manual', message: 'This is required field' }, { shouldFocus: true });
          }
        });
      }

      setIsError(true);
      setTimeout(() => {
        setIsError(false);
        setErrorMsg('');
      }, 5000);
    },
  });

  const onSubmit = (data: SchemaType) => {
    const cleanData = from !== FactFindType.AI;
    const personalDetailsData: ICaptureFactFindPersonalDetailsRequest = createPayload(data, cleanData);

    const payload: ICaptureFactFindPersonalDetailsRequest = { clientID: state.clientData.clientID, ...personalDetailsData, id: state.clientData.clientID };
    setLoading(true);
    if ([FactFindType.FACT_FIND, FactFindType.PARA_PLANNER].includes(from)) {
      capturePersonalDetails.mutate(payload);
    }
    if (from === FactFindType.AI) {
      const payloadAI = {
        section: InteractionSubSectionType.personal_details,
        data: payload,
        client_id: state.clientData.clientID,
        use_client_address_for_partner: sameAsClient,
      };
      capturePersonalDetailsAI.mutate(payloadAI);
    }
  };

  const isDirtyInPage = methods.formState.isDirty;

  useEffect(() => {
    if (isDirtyInPage) {
      updateDirtyFieldsState(true);
    }
    if (Object.keys(methods.formState.dirtyFields).length) setShowModal(true);
    else setShowModal(false);
  }, [isDirtyInPage, updateDirtyFieldsState]);

  const blocker = useBlocker(({ nextLocation, currentLocation }) => {
    return isDirtyInPage && currentLocation.pathname !== nextLocation.pathname;
  });

  useEffect(() => {
    if (blocker.state === 'blocked' && showPrompt && showModal) {
      setConfirmNavigationModal(true);
    } else {
      blocker?.proceed?.();
    }
  }, [blocker, showPrompt]);

  const handleDiscardAndProceed = () => {
    setConfirmNavigationModal(false);
    blocker?.proceed?.();
    setShowPrompt(false);
    updateDirtyFieldsState(false);
    setShowModal(false);
  };

  const handleCloseNavigationModal = () => {
    setConfirmNavigationModal(false);
    blocker?.reset?.();
    setShowPrompt(true);
  };

  const PersonalInfoTableHead = [
    { label: 'Information', colSpan: 2, show: true, width: '20%' },
    { label: 'Details', show: true },
  ];

  const partnerEmail = clientData?.clientData?.data?.partnerDetails?.personalPiiDetails?.email;
  const disableEmail = currentTab === 'client';
  const disableMobile = currentTab === 'client';

  const basicDetailsFields: IGeneralFieldType[] = [
    {
      label: 'Title',
      name: 'title',
      component: RHFAutocomplete,
      options: titleOptions,
      shouldDirty: true,
      show: ffConfig?.basicDetails?.fields?.tite?.active ?? true,
    },
    { label: 'First Name', name: 'firstname', component: RHFTextField, show: ffConfig?.basicDetails?.fields?.name?.active ?? true },
    { label: 'Middle Name', name: 'middlename', component: RHFTextField, show: ffConfig?.basicDetails?.fields?.name?.active ?? true },
    { label: 'Last Name', name: 'lastname', component: RHFTextField, show: ffConfig?.basicDetails?.fields?.name?.active ?? true },
    { label: 'Date of Birth', name: 'dob', component: RHFDatePicker, show: ffConfig?.basicDetails?.fields?.dob?.active ?? true },
    { label: 'Age', name: 'age', component: RHFTextField, disabled: true, type: 'number', show: ffConfig?.basicDetails?.fields?.age?.active ?? true },
    {
      label: 'Email ID',
      name: 'email',
      component: RHFTextField,
      type: 'email',
      show: ffConfig?.basicDetails?.fields?.email?.active ?? true,
      disabled: disableEmail,
    },
    { label: 'Gender', name: 'gender', component: RHFAutocomplete, options: genderOptions, show: ffConfig?.basicDetails?.fields?.gender?.active ?? true },
    { label: 'Nationality', name: 'nationality', component: RHFTextField, show: ffConfig?.basicDetails?.fields?.nationality?.active ?? true },
    {
      label: 'Is US Citizen?',
      name: 'isUSCitizen',
      component: RHFRadioGroup,
      options: yesNoObject,
      row: true,
      show: ffConfig?.basicDetails?.fields?.isUSCitizen?.active ?? true,
    },
    { label: 'Citizenship', name: 'citizenship', component: RHFTextField, show: ffConfig?.basicDetails?.fields?.citizenship?.active ?? true },
    {
      label: 'Country of Birth',
      name: 'countryOfBirth',
      component: RHFAutocomplete,
      options: countryOfBirthOptions,
      show: ffConfig?.basicDetails?.fields?.countryOfBirth?.active ?? true,
    },
    { label: 'Place of Birth', name: 'placeOfBirth', component: RHFTextField, show: ffConfig?.basicDetails?.fields?.placeOfBirth?.active ?? true },
    { label: 'National Insurance Number', name: 'niNumber', component: RHFTextField, show: ffConfig?.basicDetails?.fields?.niNumber?.active ?? true },
    {
      label: 'Marital Status',
      name: 'marital',
      component: RHFAutocomplete,
      options: maritalStatusOptions ?? [],
      show: ffConfig?.basicDetails?.fields?.marital?.active ?? true,
    },
    { label: 'Telephone Number', name: 'phone', component: RHFTextField, show: ffConfig?.basicDetails?.fields?.phone?.active ?? true },
    { label: 'Mobile Number', name: 'mobile', component: RHFTextField, show: ffConfig?.basicDetails?.fields?.mobile?.active ?? true, disabled: disableMobile },
    { label: 'Date of death', name: 'dateOfDeath', component: RHFDatePicker, show: ffConfig?.basicDetails?.fields?.dateOfDeath?.active ?? true },
  ];

  const employmentDetailsFields: IGeneralFieldType[] = [
    {
      label: 'Employment Status',
      name: 'employmentStatus',
      component: RHFAutocomplete,
      options: employmentStatusOptions ?? [],
      show: ffConfig?.employmentDetails?.fields?.employmentStatus?.active ?? true,
    },
    { label: 'Employer Name', name: 'employerName', component: RHFTextField, show: ffConfig?.employmentDetails?.fields?.employerName?.active ?? true },
    { label: 'Employment Type', name: 'employmentType', component: RHFTextField, show: ffConfig?.employmentDetails?.fields?.employmentType?.active ?? true },
    { label: 'Job Title', name: 'jobTitle', component: RHFTextField, show: ffConfig?.employmentDetails?.fields?.jobDescription?.active ?? true },
    { label: 'Job Description', name: 'jobDescription', component: RHFTextField, show: ffConfig?.employmentDetails?.fields?.jobDescription?.active ?? true },
    {
      label: 'Start Date',
      name: 'employmentStartDate',
      component: RHFDatePicker,
      show: ffConfig?.employmentDetails?.fields?.employmentStartDate?.active ?? true,
    },
    { label: 'End Date', name: 'employmentEndDate', component: RHFDatePicker, show: ffConfig?.employmentDetails?.fields?.employmentEndDate?.active ?? true },
    { label: 'Department', name: 'department', component: RHFTextField, show: ffConfig?.employmentDetails?.fields?.department?.active ?? true },
    { label: 'Work Location', name: 'workLocation', component: RHFTextField, show: ffConfig?.employmentDetails?.fields?.workLocation?.active ?? true },
  ];

  const addressFields: IGeneralFieldType[] = [
    { label: 'Address Type', name: 'addressType', component: RHFTextField, show: ffConfig?.addressDetails?.fields?.addressType?.active ?? true },
    { label: 'Address Line 1', name: 'addressLine1', component: RHFTextField, show: ffConfig?.addressDetails?.fields?.addressLine1?.active ?? true },
    { label: 'Address Line 2', name: 'addressLine2', component: RHFTextField, show: ffConfig?.addressDetails?.fields?.addressLine2?.active ?? true },
    { label: 'City', name: 'city', component: RHFTextField, show: ffConfig?.addressDetails?.fields?.city?.active ?? true },
    { label: 'Country', name: 'country', component: RHFTextField, show: ffConfig?.addressDetails?.fields?.country?.active ?? true },
    { label: 'Postal Code', name: 'postcode', component: RHFTextField, show: ffConfig?.addressDetails?.fields?.postcode?.active ?? true },
  ];

  const residencyFields: IGeneralFieldType[] = [
    {
      label: 'Country Of Residency',
      name: 'country',
      component: RHFAutocomplete,
      options: countries?.map((e) => e.label) || [],
      show: ffConfig?.residencyDetails?.fields?.country?.active ?? true,
    },
    { label: 'TIN Number', name: 'tin', component: RHFTextField, show: ffConfig?.residencyDetails?.fields?.tin?.active ?? true },
    {
      label: 'Residency Type',
      name: 'residencyType',
      component: RHFAutocomplete,
      options: residencyTypeOptions,
      show: ffConfig?.residencyDetails?.fields?.residencyType?.active ?? true,
    },
    {
      label: 'Is This Your Primary Residency',
      name: 'isPrimary',
      component: RHFRadioGroup,
      options: yesNoObject,
      row: true,
      show: ffConfig?.residencyDetails?.fields?.isPrimary?.active ?? true,
    },
  ];

  const handleChangeTab = (event: SyntheticEvent, value: string) => {
    setCurrentTab(value);
  };

  const handleChangeSection = (event: SyntheticEvent, value: string) => {
    setSelectSubSection(value);
  };

  const DEPENDANT_TABLE_DETAILS = [
    {
      label: 'Full Name',
      name: 'fullName',
      component: RHFTextField,
      minWidth: 150,
      width: 150,
      show: ffConfig?.dependentDetails?.fields?.fullName?.active ?? true,
    },
    {
      label: 'Date of Birth',
      name: 'dateOfBirth',
      component: RHFDatePicker,
      minWidth: 150,
      width: 150,
      show: ffConfig?.dependentDetails?.fields?.dateOfBirth?.active ?? true,
    },
    {
      label: 'Age',
      name: 'age',
      component: RHFTextField,
      disabled: true,
      minWidth: 150,
      width: 150,
      show: ffConfig?.dependentDetails?.fields?.age?.active ?? true,
    },
    {
      label: 'Gender',
      name: 'gender',
      component: RHFAutocomplete,
      options: genderOptions,
      minWidth: 150,
      width: 150,
      show: ffConfig?.dependentDetails?.fields?.gender?.active ?? true,
    },
    {
      label: 'Institution',
      name: 'schoolName',
      component: RHFTextField,
      minWidth: 150,
      width: 150,
      show: ffConfig?.dependentDetails?.fields?.schoolName?.active ?? true,
    },
    {
      label: 'Start Date',
      name: 'startDate',
      component: RHFDatePicker,
      minWidth: 150,
      width: 150,
      show: ffConfig?.dependentDetails?.fields?.startDate?.active ?? true,
    },
    {
      label: 'End Date',
      name: 'endDate',
      component: RHFDatePicker,
      minWidth: 150,
      width: 150,
      show: ffConfig?.dependentDetails?.fields?.endDate?.active ?? true,
    },
    { label: '', name: 'action', component: RHFTextField, minWidth: 50, width: 50, show: true },
  ];

  const fetchFieldArrayByType = (fieldArrayType: 'employment' | 'address' | 'residency') => {
    switch (fieldArrayType) {
      case 'address':
        return { client: addressFieldArrayClient, partner: addressFieldArrayPartner };
      case 'employment':
        return { client: employmentFieldArrayClient, partner: employmentFieldArrayPartner };
      case 'residency':
        return { client: taxResidencyFieldArrayClient, partner: taxResidencyFieldArrayPartner };
    }
  };

  const fetchFieldArray = (tab: 'client' | 'partner', fieldArrayType: 'employment' | 'address' | 'residency') => {
    switch (tab) {
      case 'client':
        return fetchFieldArrayByType(fieldArrayType).client;
      case 'partner':
        return fetchFieldArrayByType(fieldArrayType).partner;
    }
  };

  const tabSections: ('client' | 'partner')[] = ['client', 'partner'];

  const getClientsOptions = tabSections.map((type) => {
    if (type === 'partner' && !(clientPartnerDetails?.partnerDetail || updatedClientPartnerDetails?.partnerDetail) && !isClient) {
      return { label: '+ Add Partner', value: 'addPartner' };
    }
    return {
      label: getLabel(
        { value: type, label: type.charAt(0).toUpperCase() + type.slice(1) },
        [FactFindType.FACT_FIND, FactFindType.PARA_PLANNER].includes(from) && !isClient ? clientPartnerDetails : updatedClientPartnerDetails
      ),
      value: type,
    };
  });

  const selectedUser = getClientsOptions.find((option) => option.value === currentTab);
  const personalDetailsSections = [
    {
      label: 'Basic Details',
      key: ISubSectionType.basic,
      icon: BasicDetailsIcon,
      show: ffConfig?.basicDetails?.active ?? true,
    },
    { label: 'Employment Details', key: ISubSectionType.employment, icon: EmploymentIcon, errors: false, show: ffConfig?.employmentDetails?.active ?? true },
    { label: 'Residency Details', key: ISubSectionType.residency, icon: ResidencyIcon, errors: false, show: ffConfig?.residencyDetails?.active ?? true },
    { label: 'Address Details', key: ISubSectionType.address, icon: AddressIcon, errors: false, show: ffConfig?.addressDetails?.active ?? true },
    {
      label: 'Dependents',
      key: ISubSectionType.dependent,
      icon: DependentsIcon,
      errors: Object.keys(methods.formState.errors?.dependents ?? {}).length > 0,
      show: ffConfig?.dependentDetails?.active ?? true,
    },
  ];

  useEffect(() => {
    if (sameAsClient && clientAddress?.length) {
      methods.setValue(`partner.addresses.0.addressType`, clientAddress[0]?.addressType);
      methods.setValue(`partner.addresses.0.addressLine1`, clientAddress[0]?.addressLine1);
      methods.setValue(`partner.addresses.0.addressLine2`, clientAddress[0]?.addressLine2);
      methods.setValue(`partner.addresses.0.city`, clientAddress[0]?.city);
      methods.setValue(`partner.addresses.0.country`, clientAddress[0]?.country);
      methods.setValue(`partner.addresses.0.postcode`, clientAddress[0]?.postcode);
    }
  }, [sameAsClient, clientAddress]);

  return (
    <Box>
      {isTranscript.value && <Transcript section="PersonalDetails" isTranscript={isTranscript} />}
      {isAllTranscripts.value && <AllTranscripts isAllTranscripts={isAllTranscripts} />}
      {!isAllTranscripts.value && !isTranscript.value && (
        <FormProvider methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
          {viewPersonalDetails?.isPending || sourceControlPending ? (
            <FullPageSpinner />
          ) : (
            <Stack spacing={5} pb={104 / 8}>
              <Stack spacing={3}>
                <Stack>
                  <Stack direction="row" alignItems="center">
                    <FactFindHeading
                      sectionName="Personal Information"
                      isAllTranscripts={isAllTranscripts}
                      isTranscript={isTranscript}
                      hideButton={sourceControlData?.dataSourceMap?.PersonalDetails === 'ManualInput'}
                      hideAllButton={isClient || [FactFindType.AI, FactFindType.PARA_PLANNER].includes(from) || isManagedClient}
                    />
                    {conflictData?.length > 0 && (
                      <Tooltip title="Click here to view all the conflicts">
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => {
                            setConflictsDialog(true);
                          }}
                          sx={{ marginTop: '-12px' }}
                        >
                          <WarningRounded />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>

                  {fieldErrors?.length > 0 && <ErrorSection errors={fieldErrors} />}

                  <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                    <Autocomplete
                      options={
                        from === FactFindType.PARA_PLANNER
                          ? getClientsOptions.filter((e) => !['Client', 'Partner', 'Joint'].includes(e.label))
                          : getClientsOptions
                      }
                      id="autocomplete"
                      value={selectedUser?.label ?? ''}
                      sx={{ width: 300 }}
                      onChange={(e, v) => {
                        if (v?.value === 'addPartner') setAddPartnerModal(true);
                        else handleChangeTab(e, v?.value);
                      }}
                      getOptionLabel={(option) => option?.label || option}
                      renderOption={(props, option) => (
                        <li {...props}>
                          <ListItemText primary={option.label} />
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          slotProps={{
                            input: {
                              ...params.InputProps,
                              endAdornment: <InputAdornment position="end">{params?.InputProps?.endAdornment}</InputAdornment>,
                            }
                          }}
                        />
                      )}
                      disableClearable={true}
                    />

                    <Tabs
                      sx={{
                        borderBottom: '2px solid rgba(3, 5, 7, 0.08)',
                        '& .MuiTabs-scrollButtons': {
                          opacity: 0.3,
                          visibility: 'visible',
                        },
                        '& .MuiTabs-scrollButtons.Mui-disabled': {
                          opacity: 0.3,
                        },
                      }}
                      value={selectedSubSection}
                      onChange={handleChangeSection}
                      scrollButtons="auto"
                      allowScrollButtonsMobile
                    >
                      {personalDetailsSections.map((section) => {
                        if (!section.show) return null;
                        return (
                          <Tab
                            key={section.key}
                            value={section.key}
                            id={section.label}
                            label={
                              <Stack direction="row" alignItems="center" spacing={1}>
                                {section.label}
                              </Stack>
                            }
                            iconPosition="start"
                          />
                        );
                      })}
                    </Tabs>
                  </Stack>

                  <Stack spacing={1}>
                    {tabSections.map(
                      (tab) =>
                        currentTab === tab && (
                          <Fragment key={tab}>
                            {(ffConfig?.basicDetails?.active ?? true) && selectedSubSection === ISubSectionType.basic && (
                              <>
                                <Stack marginTop={2.5} marginBottom={1}>
                                  <Typography variant="h6" id="basic-details-title">
                                    Basic Details
                                  </Typography>
                                </Stack>
                                <CommonTableLayout tableHead={PersonalInfoTableHead}>
                                  <>
                                    {basicDetailsFields.map((fields) => {
                                      const DOB = methods.watch(`${currentTab}.dob`);
                                      if (dayjs(DOB).isValid()) methods.setValue(`${currentTab}.age`, dayjs().diff(dayjs(DOB), 'year').toString());

                                      return renderGeneralFields(fields, currentTab, undefined, isReadOnly);
                                    })}
                                  </>
                                </CommonTableLayout>
                              </>
                            )}

                            {(ffConfig?.employmentDetails?.active ?? true) && selectedSubSection === ISubSectionType.employment && (
                              <>
                                <Stack marginTop={2.5} marginBottom={1}>
                                  <Typography variant="h6" id="employee-details">
                                    Employment Details
                                  </Typography>
                                </Stack>

                                {isReadOnly && viewPersonalDetails?.data?.data?.[`${tab}Details`]?.employmentDetails?.length === 0 && (
                                  <Card>
                                    <Stack direction="row" justifyContent="center" alignItems="center" height="60vh">
                                      <EmptyContent description={`No employment information has been captured as part of the fact-finding process.`} />
                                    </Stack>
                                  </Card>
                                )}

                                {!(isReadOnly && viewPersonalDetails?.data?.data?.[`${tab}Details`]?.employmentDetails?.length === 0) && (
                                  <CommonTableLayout>
                                    <>
                                      <RenderArrayFieldWithChildren
                                        fieldsArray={fetchFieldArray(tab, 'employment').fields}
                                        fieldLabel="employmentDetails"
                                        arrayName="Employment"
                                        fieldArrayType={employmentDetailsFields}
                                        currentTab={currentTab}
                                        onRemove={fetchFieldArray(tab, 'employment').remove}
                                        isClient={isClient}
                                        isReadOnly={isReadOnly}
                                      />
                                    </>
                                  </CommonTableLayout>
                                )}

                                {!isReadOnly && (
                                  <Stack flexDirection="row" marginTop={1}>
                                    <Button
                                      variant="text"
                                      color="primary"
                                      id="add-employment-button"
                                      sx={{ marginLeft: 1 }}
                                      size="small"
                                      startIcon={<PlusIcon />}
                                      onClick={() => {
                                        const appendFunction = fetchFieldArray(tab, 'employment').append;

                                        appendFunction({
                                          jobTitle: '',
                                          employerName: '',
                                          employmentStatus: '',
                                          employmentType: '',
                                          jobDescription: '',
                                          employmentStartDate: '',
                                          employmentEndDate: '',
                                          department: '',
                                          workLocation: '',
                                          show: true,
                                        });
                                      }}
                                    >
                                      Add Employment
                                    </Button>
                                  </Stack>
                                )}
                              </>
                            )}

                            {(ffConfig?.residencyDetails?.active ?? true) && selectedSubSection === ISubSectionType.residency && (
                              <>
                                <Stack marginTop={2.5} marginBottom={1}>
                                  <Typography variant="h6">Residency Details</Typography>
                                </Stack>

                                {isReadOnly &&
                                  (viewPersonalDetails?.data?.data?.[`${tab}Details`]?.taxDetails?.length === 0 ||
                                    !viewPersonalDetails?.data?.data?.[`${tab}Details`]?.taxDetails) && (
                                    <Card>
                                      <Stack direction="row" justifyContent="center" alignItems="center" height="60vh">
                                        <EmptyContent description={`No residency information has been captured as part of the fact-finding process.`} />
                                      </Stack>
                                    </Card>
                                  )}

                                {!(
                                  isReadOnly &&
                                  (viewPersonalDetails?.data?.data?.[`${tab}Details`]?.taxDetails?.length === 0 ||
                                    !viewPersonalDetails?.data?.data?.[`${tab}Details`]?.taxDetails)
                                ) && (
                                  <CommonTableLayout>
                                    <>
                                      <RenderArrayFieldWithChildren
                                        fieldsArray={fetchFieldArray(tab, 'residency').fields}
                                        fieldLabel="taxResidency"
                                        arrayName="Residency"
                                        fieldArrayType={residencyFields}
                                        currentTab={currentTab}
                                        onRemove={fetchFieldArray(tab, 'residency').remove}
                                        isClient={isClient}
                                        isReadOnly={isReadOnly}
                                      />
                                    </>
                                  </CommonTableLayout>
                                )}
                                {!isClient && !isReadOnly && (
                                  <Stack flexDirection="row" marginTop={1}>
                                    <Button
                                      variant="text"
                                      color="primary"
                                      id="addResidencyButton"
                                      sx={{ marginLeft: 1 }}
                                      size="small"
                                      startIcon={<PlusIcon />}
                                      onClick={() => {
                                        const appendFunction = fetchFieldArray(tab, 'residency').append;

                                        appendFunction({ country: '', tin: '', residencyType: '', isPrimary: '', show: true });
                                      }}
                                    >
                                      Add Residency
                                    </Button>
                                  </Stack>
                                )}
                              </>
                            )}

                            {(ffConfig?.addressDetails?.active ?? true) && selectedSubSection === ISubSectionType.address && (
                              <>
                                <Stack marginTop={2.5} marginBottom={1}>
                                  <Typography variant="h6">Address Details</Typography>
                                </Stack>

                                {isReadOnly &&
                                  (viewPersonalDetails?.data?.data?.[`${tab}Details`]?.addresses?.length === 0 ||
                                    viewPersonalDetails?.data?.data?.[`${tab}Details`]?.addresses === null) && (
                                    <Card>
                                      <Stack direction="row" justifyContent="center" alignItems="center" height="60vh">
                                        <EmptyContent description={`No address information has been captured as part of the fact-finding process.`} />
                                      </Stack>
                                    </Card>
                                  )}

                                {!(
                                  isReadOnly &&
                                  (viewPersonalDetails?.data?.data?.[`${tab}Details`]?.addresses?.length === 0 ||
                                    viewPersonalDetails?.data?.data?.[`${tab}Details`]?.addresses === null)
                                ) && (
                                  <CommonTableLayout>
                                    <>
                                      <RenderArrayFieldWithChildren
                                        fieldsArray={fetchFieldArray(tab, 'address').fields}
                                        fieldLabel="addresses"
                                        arrayName="Address"
                                        fieldArrayType={addressFields}
                                        currentTab={currentTab}
                                        onRemove={fetchFieldArray(tab, 'address').remove}
                                        isClient={isClient}
                                        isReadOnly={isReadOnly}
                                        showToggle={selectedUser?.value === 'partner'}
                                        toggleFieldName={`partner.addresses.0.sameAsClient`}
                                        disableEditAddress={sameAsClient}
                                      />
                                    </>
                                  </CommonTableLayout>
                                )}
                                {!isClient && !isReadOnly && (
                                  <Stack flexDirection="row" marginTop={1}>
                                    <Button
                                      variant="text"
                                      color="primary"
                                      id="addAddressButton"
                                      sx={{ marginLeft: 1 }}
                                      size="small"
                                      startIcon={<PlusIcon />}
                                      onClick={() => {
                                        const appendFunction = fetchFieldArray(tab, 'address').append;

                                        appendFunction({ addressLine1: '', addressLine2: '', city: '', country: '', postcode: '', show: true });
                                      }}
                                    >
                                      Add Address
                                    </Button>
                                  </Stack>
                                )}
                              </>
                            )}
                          </Fragment>
                        )
                    )}
                  </Stack>
                </Stack>

                {(ffConfig?.dependentDetails?.active ?? true) && selectedSubSection === ISubSectionType.dependent && (
                  <>
                    <Stack direction="column" spacing={2}>
                      <Typography variant="h6">Dependents</Typography>

                      {isReadOnly &&
                        (viewPersonalDetails?.data?.data?.dependentDetails?.length === 0 || viewPersonalDetails?.data?.data?.dependentDetails === null) && (
                          <Card>
                            <Stack direction="row" justifyContent="center" alignItems="center" height="60vh">
                              <EmptyContent description={`No dependents information has been captured as part of the fact-finding process.`} />
                            </Stack>
                          </Card>
                        )}

                      {!(
                        isReadOnly &&
                        (viewPersonalDetails?.data?.data?.dependentDetails === null || viewPersonalDetails?.data?.data?.dependentDetails?.length === 0)
                      ) && (
                        <SimpleFieldsTable
                          fieldArray={dependentFields}
                          currentTab="dependents"
                          tableDetails={DEPENDANT_TABLE_DETAILS}
                          onRemove={remove}
                          methods={methods}
                          isClient={isClient}
                        />
                      )}
                    </Stack>
                    {!isClient && !isReadOnly && (
                      <Button
                        variant="text"
                        color="primary"
                        sx={{ width: 'max-content' }}
                        startIcon={
                          <SvgIcon>
                            <AddSvgIcon />
                          </SvgIcon>
                        }
                        size="small"
                        onClick={() => append(dependentDefaultValues)}
                      >
                        Add Dependents
                      </Button>
                    )}
                  </>
                )}
              </Stack>
            </Stack>
          )}

          {isClient ? null : (
            <>
              {[FactFindType.FACT_FIND, FactFindType.AI].includes(from) && (
                <FactFindFooter isManagedClient={isManagedClient} from={from} hideNextStep={from === FactFindType.AI} hideSave={isReadOnly} />
              )}
              <SaveDialog open={loading} />
              <SuccessDialog open={isSuccess} />
              <ErrorDialog open={isError} message={errorMsg} />
              <ConfirmNavigationDialog open={confirmNavigationModal} handleClose={handleCloseNavigationModal} data={{ handleDiscardAndProceed, blocker }} />
            </>
          )}
        </FormProvider>
      )}
      <AddPartnerDialog
        open={addPartnerModal}
        onClose={() => setAddPartnerModal(false)}
        clientID={state.clientData.clientID}
        sourceControlMutateAsync={sourceControlMutateAsync}
        refetch={refetch}
        getDropdownOptions={getDropdownOptions}
      />
      <ConflictsDialog
        open={conflictsDialog}
        handleClose={() => setConflictsDialog(false)}
        tableData={conflictData || []}
        isPending={false}
        title={'Personal Information'}
        refetch={refetch}
      />
    </Box>
  );
};
